/* eslint-disable @next/next/no-img-element */
import React, { useRef, useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Breadcrumbs } from 'src/components/Breadcrumbs'
import { SkyscraperBanner } from 'src/components/Banners'
import { BaseLayout } from 'src/layouts'
import { useNewsArticle, useNewsArticles } from 'src/state'
import { useAppIntl } from 'src/i18n'
import { LastNewArticles } from './components/LastNewsArticles'
import ListAuthor from 'src/components/ListAuthor'
import { StickyContentNav } from 'src/components/StickyContentNav'
import {
  MAIN_SITE_IMAGE,
  SITENAME,
  WEBSITE,
  newsArticleRouteCategory,
  newsArticlesRoute,
} from 'src/consts'
import { Container } from 'src/elements'
import styles from './styles.module.scss'
import { Chip, Tag } from 'src/360ui'
import { NewsArticle } from 'schema-dts'
import { splitContentByPageBreak } from 'src/utils/content'

const defaultMainImage = {
  url: '/assets/news-markets.jpg',
}

export type NewsArticleDetailsProps = {
  slug: string
}

export const NewsArticleDetails = ({ slug }: NewsArticleDetailsProps) => {
  const intl = useAppIntl()
  const ref = useRef(null)
  const newsArticle = useNewsArticle({ slug }).data
  const lastNewsArticles = useNewsArticles({ page: 1, pageSize: 5 }).data?.data

  const contentSegments = splitContentByPageBreak(
    newsArticle.attributes.content || ''
  )

  function extractTitleFromHTML(html) {
    const regex = /<h2[^>]*>(.*?)<\/h2>/
    const match = html.match(regex)
    if (match && match[1]) {
      let titleWithAttributesRemoved = match[1].replace(/<[^>]+>/g, '')

      titleWithAttributesRemoved = titleWithAttributesRemoved.replace(
        /&nbsp;/g,
        ''
      )
      const titleWithoutNbspAndAmp = titleWithAttributesRemoved.replace(
        /&amp;/g,
        ''
      )
      return titleWithoutNbspAndAmp
    }
    return ''
  }

  const links = contentSegments.map((segment, index) => {
    const anchorTitle = extractTitleFromHTML(segment) || `Section ${index + 1}`
    const anchorId = `section-${index + 1}`
    return {
      title: anchorTitle,
      targetId: anchorId,
    }
  })
  const newsBreadcrumbs = [{ breadcrumb: 'News', href: newsArticlesRoute() }]
  const mainImage =
    newsArticle.attributes.mainImage?.data?.attributes || defaultMainImage

  const categories = newsArticle.attributes.categories?.data || []

  const [modalImage, setModalImage] = useState(null)

  const openModal = imageUrl => {
    setModalImage(imageUrl)
  }

  const closeModal = () => {
    setModalImage(null)
  }

  useEffect(() => {
    const handleImgClick = event => {
      const imageUrl = event.target.getAttribute('src')
      openModal(imageUrl)
    }

    // Attach event listener to images within the content when component mounts
    const images = document.querySelectorAll('.news-content img')
    images.forEach(img => {
      img.addEventListener('click', handleImgClick)
    })

    // Cleanup function to remove event listeners when component unmounts
    return () => {
      images.forEach(img => {
        img.removeEventListener('click', handleImgClick)
      })
    }
  }, [contentSegments]) // Re-run effect when contentSegments change

  const [imageHeight, setImageHeight] = useState(0)

  useEffect(() => {
    const calculateImageHeight = () => {
      const aspectRatio = 16 / 9
      const containerWidth = ref.current.offsetWidth
      const calculatedHeight = containerWidth / aspectRatio
      setImageHeight(calculatedHeight)
    }

    calculateImageHeight()
    const handleResize = () => {
      calculateImageHeight()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.leftColumn}>
        <Breadcrumbs
          items={newsBreadcrumbs}
          lastItem={newsArticle?.attributes.title}
          wrapperClassName={styles.breadcrumbs}
        />
        <article className={styles.newsArticle}>
          <div className={styles.header}>
            <div>
              <h1 className={styles.title}>{newsArticle.attributes.title}</h1>
              <ListAuthor
                authorData={{
                  authorSlug:
                    newsArticle?.attributes?.author?.data?.attributes.slug,
                  authorImage:
                    newsArticle?.attributes?.author?.data?.attributes?.image
                      ?.data?.attributes?.url,
                  authorFullName:
                    newsArticle?.attributes?.author?.data?.attributes?.fullName,
                  createdAt: newsArticle?.attributes?.publishedAt,
                }}
              />
              <div className={styles.tags}>
                <Tag className={styles.date}>
                  {intl.timeago(new Date(newsArticle.attributes.publishedAt))}
                </Tag>
                <span className={styles.timeRead}>
                  {newsArticle.attributes.readTime} read
                </span>
              </div>
              {categories.length > 0 && (
                <div className={styles.categories}>
                  {categories.map(category => {
                    const icon = category.attributes.icon?.data?.attributes?.url
                    return (
                      <Link
                        key={category.id}
                        href={newsArticleRouteCategory(
                          category.attributes?.slug
                        )}
                        passHref
                      >
                        <Chip
                          key={category.id}
                          icon={
                            icon && (
                              <Image src={icon} alt='' width={16} height={16} />
                            )
                          }
                        >
                          {category.attributes.name}
                        </Chip>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
            <div className={styles.imageContainer}>
              <div className={styles.img} ref={ref}>
                <img
                  src={mainImage?.url}
                  alt={mainImage?.alternativeText}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onClick={() => openModal(mainImage?.url)}
                />
              </div>
            </div>
          </div>
          <div className={styles.content}>
            {contentSegments.map((segment, index) => (
              <div
                key={index}
                id={`section-${index + 1}`}
                className={`${
                  index === 0 ? styles.firstContent : styles.otherContent
                } ${
                  index === contentSegments.length - 1 ? styles.lastContent : ''
                }`}
              >
                <div
                  className='news-content'
                  dangerouslySetInnerHTML={{ __html: segment }}
                />
              </div>
            ))}
          </div>
        </article>
      </div>
      <div className={styles.rightColumn}>
        {lastNewsArticles && (
          <div className={styles.latestNews}>
            <LastNewArticles
              className={styles.lastNewsArticles}
              newsArticles={lastNewsArticles}
            />
          </div>
        )}
        <aside className={styles.stickyNav}>
          <StickyContentNav links={links} />
        </aside>
        <SkyscraperBanner />
      </div>
      {modalImage && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent}>
            <span className={styles.closeButton} onClick={closeModal}>
              &times;
            </span>
            <img
              src={modalImage}
              alt={modalImage}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const NewsArticleDetailsLayout = ({
  children,
  slug,
}: GetLayoutProps<NewsArticleDetailsProps>) => {
  const newsArticle = useNewsArticle({ slug }).data

  const structuredData: NewsArticle = {
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${WEBSITE}/news/${slug}`,
    },
    headline: newsArticle.attributes.title,
    image: newsArticle.attributes.mainImage?.data?.attributes?.url,
    datePublished: newsArticle.attributes.publishedAt,
    dateModified: newsArticle.attributes.updatedAt,
    author: {
      '@type': 'Person',
      name: newsArticle.attributes.author?.data?.attributes.fullName,
    },

    publisher: {
      '@type': 'Organization',
      name: 'COIN360',
      logo: {
        '@type': 'ImageObject',
        url: `${WEBSITE}/assets/share/index.png`,
      },
    },
    description: newsArticle.attributes.seo?.metaDescription,
  }

  return (
    <BaseLayout
      scrollInContent
      seo={{
        title:
          newsArticle.attributes.seo?.metaTitle || newsArticle.attributes.title,
        description: newsArticle.attributes.seo?.metaDescription,
        keywords: newsArticle?.attributes.keywords,
        robots: newsArticle?.attributes.metaRobots,
        canonical: `${WEBSITE}/news/${slug}`,
        jsonLd: structuredData,
        image: newsArticle?.attributes.mainImage?.data?.attributes?.url,
      }}
    >
      {children}
    </BaseLayout>
  )
}

NewsArticleDetails.getLayout = NewsArticleDetailsLayout
